import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Nav, Navbar, Offcanvas } from 'react-bootstrap';
import React, { useState, useEffect, useRef } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import sustainabilityHubLogo from '@/assets/img/logos/png/blueBackground.png';
import profileIcon from '@/assets/img/icons/svg/profileIcon.svg';
import listIcon from '@/assets/img/icons/svg/list.svg';
import arrowDropDown from '@/assets/img/icons/svg/arrowDropDown.svg';
import xIcon from '@/assets/img/icons/svg/x.svg';
import useFullAuth from '@/features/auth/hooks/useFullAuth';
import useModeratorCommunities from '@/hooks/useModeratorCommunities';
import './NavBar.css';

export default function NavBar() {
    const [expanded, setExpanded] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [showMobileAccountSection, setShowMobileAccountSection] =
        useState(true);
    const [scrolled, setScrolled] = useState(false);
    const dropdownRefs = useRef({});
    const scrollSentinelRef = useRef(null);

    const { logout, user, isAdmin } = useAuth();
    const { user: fullyAuthenticatedUser } = useFullAuth();
    const { isModerator } = useModeratorCommunities();
    const navigate = useNavigate();
    const location = useLocation();

    // Only the home page has a full-bleed hero to show through a transparent
    // nav; every other page keeps today's solid nav, unchanged.
    const isHomePage = location.pathname === '/';
    const isTransparent = isHomePage && !scrolled;

    // Use either fully authenticated user OR basic user for mobile display
    const isLoggedIn = fullyAuthenticatedUser || user;

    const handleNavClick = () => {
        setExpanded(false);
        setActiveDropdown(null);
    };

    const toggleDropdown = (dropdownName) => {
        // If clicking the same dropdown, close it
        if (activeDropdown === dropdownName) {
            setActiveDropdown(null);
        } else {
            // If clicking a different dropdown, switch to it immediately
            setActiveDropdown(dropdownName);
        }
    };

    // Helper functions for dropdown toggle handlers
    const handleDropdownToggle = (dropdownName) => {
        toggleDropdown(dropdownName);
    };

    const handleDropdownKeyDown = (e, dropdownName) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleDropdown(dropdownName);
        }
    };

    const handleLogout = async () => {
        await logout();
        setActiveDropdown(null);
        navigate('/');
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if clicking outside nested dropdowns
            if (activeDropdown) {
                const isClickInsideAnyDropdown = Object.values(
                    dropdownRefs.current
                ).some((ref) => ref && ref.contains(event.target));

                if (!isClickInsideAnyDropdown) {
                    setActiveDropdown(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, [activeDropdown]);

    // Close dropdown when scrolling
    useEffect(() => {
        const handleScroll = () => {
            if (activeDropdown) {
                setActiveDropdown(null);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeDropdown]);

    // Close dropdown/menu when pressing ESC key
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                if (activeDropdown) {
                    setActiveDropdown(null);
                }
                if (expanded) {
                    setExpanded(false);
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [activeDropdown, expanded]);

    // Handle window resize to fix responsive breakpoint behavior
    useEffect(() => {
        const handleResize = () => {
            // Close mobile menu when resizing to desktop
            if (window.innerWidth >= 992) {
                // Bootstrap lg breakpoint
                if (expanded) {
                    setExpanded(false);
                }
            }
            // Close desktop dropdowns when resizing to mobile
            if (window.innerWidth < 992) {
                if (activeDropdown && activeDropdown !== 'profile') {
                    setActiveDropdown(null);
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [expanded, activeDropdown]);

    // Detect whether the user has left the very top of the page. Only
    // meaningful on the home page (the only page with a transparent nav to
    // switch away from), so both the sentinel and its observer are gated on
    // isHomePage rather than mounting a no-op observer on every route. The
    // sentinel is a normal-flow element (not part of the fixed nav), so it
    // scrolls out of view as soon as the page moves — an IntersectionObserver
    // on it is cheaper than a scroll listener since it only fires on that
    // one transition instead of on every scroll event. Guarded for
    // environments (older browsers, some test runners) without the API.
    useEffect(() => {
        const sentinel = scrollSentinelRef.current;
        if (
            !isHomePage ||
            !sentinel ||
            typeof IntersectionObserver === 'undefined'
        ) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => setScrolled(!entry.isIntersecting),
            { threshold: 0 }
        );
        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [isHomePage]);

    return (
        <>
            {/* Normal-flow marker the scroll observer watches; stays at the
                true top of the page even when the nav below is fixed. Only
                needed on the home page — see the effect above. */}
            {isHomePage && (
                <div
                    ref={scrollSentinelRef}
                    className="navbar-scroll-sentinel"
                    aria-hidden="true"
                />
            )}
            <section id="navbar" className={isHomePage ? 'navbar-on-hero' : ''}>
                <Navbar
                    expanded={expanded}
                    onToggle={() => setExpanded(!expanded)}
                    className={`bg-body-tertiary${isTransparent ? ' navbar-transparent' : ''}`}
                    expand="lg"
                    bg="light"
                >
                    <Container>
                        <Navbar.Brand
                            as={Link}
                            to="/"
                            className="d-flex align-items-center gap-2"
                            onClick={handleNavClick}
                        >
                            <img
                                src={sustainabilityHubLogo}
                                alt="Logo"
                                width="65"
                                height="65"
                                className="d-inline-block align-top logo-responsive"
                            />
                            <h1 className="h1 fs-1 text-primary fw-semibold navbar-brand mb-0">
                                Sustainability Hub
                            </h1>
                        </Navbar.Brand>

                        {/* Mobile hamburger - only visible on mobile */}
                        <Navbar.Toggle
                            aria-controls="offcanvas-navbar"
                            className="d-lg-none border-0"
                            aria-label="Toggle navigation"
                        >
                            <img
                                src={listIcon}
                                alt="Menu"
                                className="navbar-list-icon"
                            />
                        </Navbar.Toggle>

                        {/* Desktop/Tablet Navigation - hidden on mobile */}
                        <Navbar.Collapse
                            id="right-aligned"
                            className="d-none d-lg-flex justify-content-end"
                        >
                            <Nav className="ms-auto align-items-lg-center">
                                {/* Chat with Bili CTA */}
                                <Link
                                    to="/bili-chat"
                                    className="btn btn-warning text-primary mx-2 px-3 py-2 fw-medium rounded-pill text-center fs-5 text-nowrap mt-1"
                                    onClick={handleNavClick}
                                >
                                    Chat with Bili
                                </Link>

                                {/* Explore Dropdown */}
                                <div
                                    className={`nav-dropdown position-relative d-flex align-items-center ${activeDropdown === 'explore' ? 'show' : ''}`}
                                    ref={(el) =>
                                        (dropdownRefs.current.explore = el)
                                    }
                                >
                                    <div
                                        className="nav-dropdown-toggle d-flex align-items-center gap-2 text-primary fw-medium text-decoration-none rounded-pill bg-transparent border-0 fs-4"
                                        onClick={() =>
                                            handleDropdownToggle('explore')
                                        }
                                        onKeyDown={(e) =>
                                            handleDropdownKeyDown(e, 'explore')
                                        }
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={
                                            activeDropdown === 'explore'
                                        }
                                    >
                                        Explore
                                        <img
                                            src={arrowDropDown}
                                            alt=""
                                            className="dropdown-arrow"
                                        />
                                    </div>
                                    <div className="nav-dropdown-menu position-absolute bg-body border rounded-3 shadow-sm d-flex flex-column gap-2 p-3">
                                        <Link
                                            to="/spotlights"
                                            className="nav-dropdown-link text-primary text-decoration-none fw-normal d-block rounded-pill text-start fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Spotlights
                                        </Link>
                                        <Link
                                            to="/communities"
                                            className="nav-dropdown-link text-primary text-decoration-none fw-normal d-block rounded-pill text-start fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Communities of Interest
                                        </Link>
                                        <Link
                                            to="/data"
                                            className="nav-dropdown-link text-primary text-decoration-none fw-normal d-block rounded-pill text-start fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Data Discovery
                                        </Link>
                                    </div>
                                </div>

                                {/* About Dropdown */}
                                <div
                                    className={`nav-dropdown position-relative d-flex align-items-center ${activeDropdown === 'about' ? 'show' : ''}`}
                                    ref={(el) =>
                                        (dropdownRefs.current.about = el)
                                    }
                                >
                                    <div
                                        className="nav-dropdown-toggle d-flex align-items-center gap-2 text-primary fw-medium text-decoration-none rounded-pill bg-transparent border-0 fs-4"
                                        onClick={() =>
                                            handleDropdownToggle('about')
                                        }
                                        onKeyDown={(e) =>
                                            handleDropdownKeyDown(e, 'about')
                                        }
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={
                                            activeDropdown === 'about'
                                        }
                                    >
                                        About
                                        <img
                                            src={arrowDropDown}
                                            alt=""
                                            className="dropdown-arrow"
                                        />
                                    </div>
                                    <div className="nav-dropdown-menu position-absolute bg-body border rounded-3 shadow-sm d-flex flex-column gap-2 p-3">
                                        <Link
                                            to="/about"
                                            className="nav-dropdown-link text-primary text-decoration-none fw-normal d-block rounded-pill text-start fs-5"
                                            onClick={handleNavClick}
                                        >
                                            About Us
                                        </Link>
                                        <Link
                                            to="/project-updates"
                                            className="nav-dropdown-link text-primary text-decoration-none fw-normal d-block rounded-pill text-start fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Project Updates
                                        </Link>
                                        <Link
                                            to="/contact"
                                            className="nav-dropdown-link text-primary text-decoration-none fw-normal d-block rounded-pill text-start fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Contact Us
                                        </Link>
                                        <Link
                                            to="/policies"
                                            className="nav-dropdown-link text-primary text-decoration-none fw-normal d-block rounded-pill text-start fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Policies
                                        </Link>
                                    </div>
                                </div>

                                {/* Account Section - Logged Out */}
                                {!fullyAuthenticatedUser && !user ? (
                                    <>
                                        <Link
                                            to="/login"
                                            className="btn btn-primary nav-btn-auth mx-2 px-3 py-2 fw-medium rounded-pill text-center fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Login
                                        </Link>
                                        <Link
                                            to="/signup"
                                            className="btn btn-primary nav-btn-auth mx-2 px-3 py-2 fw-medium rounded-pill text-center fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Sign Up
                                        </Link>
                                    </>
                                ) : !fullyAuthenticatedUser && user ? (
                                    /* Account Section - Partial Auth (Firebase but no MongoDB profile) */
                                    <button
                                        onClick={handleLogout}
                                        className="btn btn-primary nav-btn-auth mx-2 px-3 py-2 fw-medium rounded-pill text-center fs-5"
                                    >
                                        Log Out
                                    </button>
                                ) : (
                                    /* Account Section - Logged In with Profile Icon */
                                    <div
                                        className={`nav-dropdown profile-dropdown position-relative d-none d-lg-flex align-items-center ${activeDropdown === 'profile' ? 'show' : ''}`}
                                        ref={(el) =>
                                            (dropdownRefs.current.profile = el)
                                        }
                                    >
                                        <div
                                            className="profile-icon-button d-flex align-items-center justify-content-center text-primary bg-transparent border-0 p-0"
                                            onClick={() =>
                                                handleDropdownToggle('profile')
                                            }
                                            onKeyDown={(e) =>
                                                handleDropdownKeyDown(
                                                    e,
                                                    'profile'
                                                )
                                            }
                                            role="button"
                                            tabIndex={0}
                                            aria-label="Profile menu"
                                            aria-expanded={
                                                activeDropdown === 'profile'
                                            }
                                        >
                                            <img
                                                src={profileIcon}
                                                alt="Profile"
                                                className="profile-svg-icon"
                                            />
                                        </div>
                                        <div className="nav-dropdown-menu profile-menu position-absolute bg-body border rounded-3 shadow-sm d-flex flex-column gap-2 p-2">
                                            <Link
                                                to="/dashboard"
                                                className="btn btn-outline-primary fw-medium d-block rounded-pill text-center profile-menu-btn fs-5"
                                                onClick={handleNavClick}
                                            >
                                                Dashboard
                                            </Link>
                                            <Link
                                                to="/profile"
                                                className="btn btn-warning text-primary fw-medium d-block rounded-pill text-center profile-menu-btn fs-5"
                                                onClick={handleNavClick}
                                            >
                                                Profile
                                            </Link>
                                            {isAdmin && (
                                                <Link
                                                    to="/admin"
                                                    className="btn btn-outline-danger fw-medium d-block rounded-pill text-center profile-menu-btn fs-5"
                                                    onClick={handleNavClick}
                                                >
                                                    Admin
                                                </Link>
                                            )}
                                            {isModerator && !isAdmin && (
                                                <Link
                                                    to="/moderator"
                                                    className="btn btn-outline-primary fw-medium d-block rounded-pill text-center profile-menu-btn fs-5"
                                                    onClick={handleNavClick}
                                                >
                                                    Moderator
                                                </Link>
                                            )}
                                            <button
                                                onClick={handleLogout}
                                                className="btn btn-primary fw-medium d-block w-100 rounded-pill text-center profile-menu-btn fs-5"
                                            >
                                                Log Out
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </Nav>
                        </Navbar.Collapse>

                        {/* Mobile Offcanvas - only visible on mobile */}
                        <Navbar.Offcanvas
                            id="offcanvas-navbar"
                            aria-labelledby="offcanvas-navbar-label"
                            placement="end"
                            className="d-lg-none w-100 mw-100 border-0"
                        >
                            <Offcanvas.Header className="border-bottom d-flex align-items-center">
                                {isLoggedIn && (
                                    <div
                                        className="profile-icon-button-mobile d-flex align-items-center justify-content-center"
                                        onClick={() =>
                                            setShowMobileAccountSection(
                                                !showMobileAccountSection
                                            )
                                        }
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                e.preventDefault();
                                                setShowMobileAccountSection(
                                                    !showMobileAccountSection
                                                );
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label="Toggle profile menu"
                                        aria-expanded={showMobileAccountSection}
                                    >
                                        <img
                                            src={profileIcon}
                                            alt="Profile"
                                            className="profile-svg-icon-mobile"
                                        />
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="offcanvas-close-btn bg-transparent border-0 p-0 ms-auto"
                                    aria-label="Close"
                                    onClick={() => setExpanded(false)}
                                >
                                    <img
                                        src={xIcon}
                                        alt="Close"
                                        className="offcanvas-close-icon"
                                    />
                                </button>
                            </Offcanvas.Header>
                            <Offcanvas.Body className="bg-primary">
                                {/* Mobile Account Section */}
                                {isLoggedIn && showMobileAccountSection && (
                                    <div className="mobile-account-section d-flex flex-column gap-1 p-3 bg-body border rounded-3 shadow-sm mb-3">
                                        <div className="d-flex flex-column gap-2 mobile-account-buttons">
                                            {fullyAuthenticatedUser && (
                                                <button
                                                    onClick={() => {
                                                        navigate('/dashboard');
                                                        setExpanded(false);
                                                    }}
                                                    className="btn btn-outline-primary fw-medium rounded-pill w-100"
                                                >
                                                    Dashboard
                                                </button>
                                            )}
                                            {/* Only show Profile button if fully authenticated (has MongoDB profile) */}
                                            {fullyAuthenticatedUser && (
                                                <button
                                                    onClick={() => {
                                                        navigate('/profile');
                                                        setExpanded(false);
                                                    }}
                                                    className="btn btn-warning text-primary fw-medium rounded-pill w-100"
                                                >
                                                    Profile
                                                </button>
                                            )}
                                            {isAdmin && (
                                                <button
                                                    onClick={() => {
                                                        navigate('/admin');
                                                        setExpanded(false);
                                                    }}
                                                    className="btn btn-outline-danger fw-medium rounded-pill w-100"
                                                >
                                                    Admin
                                                </button>
                                            )}
                                            {isModerator && !isAdmin && (
                                                <button
                                                    onClick={() => {
                                                        navigate('/moderator');
                                                        setExpanded(false);
                                                    }}
                                                    className="btn btn-outline-primary fw-medium rounded-pill w-100"
                                                >
                                                    Moderator
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    handleLogout();
                                                    setExpanded(false);
                                                }}
                                                className="btn btn-primary fw-medium rounded-pill w-100"
                                            >
                                                Log Out
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <Nav className="flex-column">
                                    {/* All Navigation Links - No Dropdowns */}
                                    <div className="p-3 bg-body border rounded-3 shadow-sm mb-3">
                                        <Link
                                            to="/bili-chat"
                                            className="btn btn-warning text-primary w-100 rounded-pill mb-2 fw-medium fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Chat with Bili
                                        </Link>
                                        <Nav.Link
                                            as={Link}
                                            to="/spotlights"
                                            className="text-primary py-2 fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Spotlights
                                        </Nav.Link>
                                        <Nav.Link
                                            as={Link}
                                            to="/communities"
                                            className="text-primary py-2 fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Communities of Interest
                                        </Nav.Link>
                                        <Nav.Link
                                            as={Link}
                                            to="/data"
                                            className="text-primary py-2 fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Data Discovery
                                        </Nav.Link>
                                        <Nav.Link
                                            as={Link}
                                            to="/about"
                                            className="text-primary py-2 fs-5"
                                            onClick={handleNavClick}
                                        >
                                            About Us
                                        </Nav.Link>
                                        <Nav.Link
                                            as={Link}
                                            to="/project-updates"
                                            className="text-primary py-2 fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Project Updates
                                        </Nav.Link>
                                        <Nav.Link
                                            as={Link}
                                            to="/contact"
                                            className="text-primary py-2 fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Contact Us
                                        </Nav.Link>
                                        <Nav.Link
                                            as={Link}
                                            to="/policies"
                                            className="text-primary py-2 fs-5"
                                            onClick={handleNavClick}
                                        >
                                            Policies
                                        </Nav.Link>
                                    </div>

                                    {/* Auth Buttons - Only when logged out */}
                                    {!isLoggedIn && (
                                        <div className="p-3 bg-body border rounded-3 shadow-sm mb-3">
                                            <div className="d-flex flex-column gap-2 mobile-auth-buttons">
                                                <Link
                                                    to="/login"
                                                    className="btn btn-primary w-100 rounded-pill"
                                                    onClick={handleNavClick}
                                                >
                                                    Login
                                                </Link>
                                                <Link
                                                    to="/signup"
                                                    className="btn btn-primary w-100 rounded-pill"
                                                    onClick={handleNavClick}
                                                >
                                                    Sign Up
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </Nav>
                            </Offcanvas.Body>
                        </Navbar.Offcanvas>
                    </Container>
                </Navbar>
            </section>
        </>
    );
}
